import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
} from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import {
  SingleJobPostingPurchaseDialogComponent,
  SingleJobPostingPurchaseDialogData,
} from '../single-job-posting-purchase-dialog/single-job-posting-purchase-dialog.component';
import { JobKind, JobKinds } from '../../../types/job';
import { RoleService } from '../../../services/role.service';

export type FreePlanJobPostingDialogVariant =
  | 'onboardingConfirm'
  | 'limitReached';

export type FreePlanJobPostingDialogData = {
  variant: FreePlanJobPostingDialogVariant;
  /**
   * Schuleinheit root school id. When set, «Jetzt upgraden» opens {@link OrderProcessComponent}
   * with `schuleinheitExistingSchoolId` — same entry as upgrading from free school to paid
   * (plan offers / checkout for an existing school, no role-selection step).
   */
  schuleinheitExistingSchoolId?: string;
  /**
   * Name of that school, used to prefill the invoice «Organisation» field so the
   * buyer is not asked to retype something we already know.
   */
  schoolName?: string | null;
  /**
   * Job kind that was about to be posted — determines which single-posting product is offered
   * (29 CHF for Stellvertretung and befristete Anstellung, 199 CHF for Festanstellung).
   */
  jobKind?: JobKind;
  /**
   * Called by the dialog AFTER a successful single-posting purchase. The caller owns the retry,
   * navigation and error handling — the dialog only signals «purchase succeeded → retry».
   */
  onPurchaseSuccess?: () => void;
};

@Component({
  selector: 'app-free-plan-job-posting-dialog',
  standalone: true,
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButton,
    MatIcon,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './free-plan-job-posting-dialog.component.html',
})
export class FreePlanJobPostingDialogComponent {
  readonly data = inject<FreePlanJobPostingDialogData>(MAT_DIALOG_DATA);
  private readonly ref = inject(
    MatDialogRef<FreePlanJobPostingDialogComponent>
  );
  private readonly dialog = inject(MatDialog);
  private readonly roleService = inject(RoleService);

  /**
   * Invited admins of someone else's organisation cannot purchase (backend
   * rejects with MEMBER_CANNOT_PURCHASE). Hide both purchase options and show
   * the «Abos verwaltet der Eigentümer» hint instead.
   */
  get isInvitedMemberOnly(): boolean {
    return this.roleService.isInvitedMemberOnly;
  }

  confirmFreeOnboarding(): void {
    this.ref.close(true);
  }

  cancelOnboarding(): void {
    this.ref.close(false);
  }

  closeLimit(): void {
    this.ref.close();
  }

  /** True when the caller provided everything needed to offer the single-posting purchase. */
  get canPurchaseSinglePosting(): boolean {
    return !!this.data.jobKind && !!this.data.onPurchaseSuccess;
  }

  /**
   * Job kinds covered by the cheaper 29 CHF single-posting license: Stellvertretung
   * and befristete Anstellung. Festanstellung uses the 199 CHF license.
   */
  private get isReducedPriceKind(): boolean {
    return (
      this.data.jobKind === JobKinds.SUBSTITUTE ||
      this.data.jobKind === JobKinds.TEMPORARY
    );
  }

  /** Price label for the single-posting button, based on the job kind. */
  get singlePostingPriceLabel(): string {
    return this.isReducedPriceKind ? '29 CHF' : '199 CHF';
  }

  purchaseSinglePosting(): void {
    if (!this.canPurchaseSinglePosting) return;

    const licenseType: SingleJobPostingPurchaseDialogData['licenseType'] = this
      .isReducedPriceKind
      ? 'SUBSTITUTION_JOB_POSTING'
      : 'PERMANENT_JOB_POSTING';

    // Hide this options dialog while the purchase dialog is shown so the two
    // dialogs don't stack visually. It stays open underneath and is revealed
    // again on cancel so the user can pick another option.
    this.ref.addPanelClass('bn-dialog-hidden');

    const purchaseRef = this.dialog.open(
      SingleJobPostingPurchaseDialogComponent,
      {
        // The school is what gives the purchase an organisation to bill: the
        // backend walks up from it to whoever pays, which is the only way a
        // free-plan buyer gets an invoice address stored at all.
        data: {
          licenseType,
          schoolId: this.data.schuleinheitExistingSchoolId,
          schoolName: this.data.schoolName,
        },
        maxWidth: '520px',
        autoFocus: false,
      }
    );

    purchaseRef.afterClosed().subscribe(purchased => {
      // Cancel → reveal the options dialog again so the user can pick another option.
      if (purchased !== true) {
        this.ref.removePanelClass('bn-dialog-hidden');
        return;
      }
      this.ref.close();
      this.data.onPurchaseSuccess!();
    });
  }

  async openUpgrade(): Promise<void> {
    const existingId = this.data.schuleinheitExistingSchoolId?.trim();
    this.ref.close();
    // Loaded lazily: OrderProcessComponent (via OrderProcessProductsComponent)
    // imports this dialog, so a static import forms a module cycle that leaves
    // OrderProcessComponent's `imports` undefined at runtime (NG0919, empty dialog).
    const { OrderProcessComponent } = await import(
      '../../../dialogs/order-process/order-process.component'
    );
    this.dialog.open(OrderProcessComponent, {
      minWidth: '100vw',
      minHeight: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      disableClose: true,
      autoFocus: false,
      ...(existingId
        ? { data: { schuleinheitExistingSchoolId: existingId } }
        : {}),
    });
  }
}
